import { type VariantProps } from "class-variance-authority";
import * as React from "react";

import CurrencyInput from "./currency-input";
import { Input } from "./input";
import { inputVariants } from "./input-variants";

export interface NumberInputProps
  extends Omit<React.ComponentProps<"input">, "type" | "value" | "onChange" | "min" | "max" | "step" | "inputMode" | "size">,
    Pick<VariantProps<typeof inputVariants>, "size"> {
  value?: number | null;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  allowDecimals?: boolean;
  allowNegative?: boolean;
  step?: number | string;
  currencyInput?: boolean;

}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      min,
      max,
      allowDecimals = true,
      allowNegative = true,
      step,
      ...props
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;

      if (inputValue === "" || inputValue === "-" || inputValue === ".") {
        onChange?.(null);
        return;
      }

      const numericValue = Number(inputValue);
      if (Number.isNaN(numericValue)) {
        return;
      }

      if (!allowNegative && numericValue < 0) {
        return;
      }

      let finalValue: number;
      if (allowDecimals) {
        finalValue = numericValue;
      } else {
        finalValue = Math.floor(Math.abs(numericValue));
        if (numericValue < 0 && !allowNegative) {
          return;
        }
      }

      if (min !== undefined && finalValue < min) {
        return;
      }
      if (max !== undefined && finalValue > max) {
        return;
      }

      onChange?.(finalValue);
    };

    const defaultStep = allowDecimals ? "0.01" : "1";
    const inputStep = step ?? defaultStep;
    const inputMode = allowDecimals ? "decimal" : "numeric";
    const Component = props.currencyInput ? CurrencyInput : Input;
    return (
      <Component
        ref={ref}
        type="number"
        inputMode={inputMode}
        step={inputStep}
        min={min}
        max={max}
        value={value === null ? "" : value}
        onChange={handleChange}
        {...props}
      />
    );
  }
);

NumberInput.displayName = "NumberInput";

export { NumberInput };